/**
 * Explorador interativo da API publica: lista endpoints por camada, valida o
 * token colado e executa as chamadas ("Tente agora").
 *
 * Compartilhado por /averbadora/api/docs (as 4 camadas) e /banco/api (so a
 * camada banco). O <header> NAO vive aqui — cada pagina renderiza o seu, como
 * manda a convencao do GUIA_EDICAO_WEB.md, e por isso o texto institucional de
 * cada perfil fica na pagina a que pertence.
 *
 * Este e o unico lugar do web que chama a API com `fetch` cru em vez do SDK, de
 * proposito: a ferramenta autentica com `Bearer atl_*` (token de integracao),
 * nao com o JWT de sessao que o SDK carrega.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button, Pill, Tabs } from "@atlas/ui/web";
import { apiBaseUrl } from "../lib/sdk";
import { RowButton } from "./RowButton";
import {
  CAMPOS_POR_ENDPOINT, CAMPOS_RESPOSTA_POR_ENDPOINT, CODIGOS_ERRO_POR_CAMADA, CRITICAS_POR_ENDPOINT, ANEXOS_POR_CAMADA,
  type CampoDoc,
} from "../lib/api-docs-schemas.generated";
import type { Endpoint, Layer, Method } from "../lib/api-docs-layers";

/** Resultado da validacao do token colado, contra /v1/external/me. */
export type TryTokenInfo =
  | { estado: "vazio" }
  | { estado: "checando" }
  | { estado: "invalido" }
  | { estado: "ok"; camada: string; partnerId: number; scopes: string[]; tokenId: string; environment: string };

export interface ApiDocsExplorerProps {
  /** Camadas navegaveis. Com 1 elemento, o seletor de camada nao e renderizado. */
  layers: Layer[];
  /** Camada aberta ao montar. Default: a primeira de `layers`. */
  layerInicial?: Layer["key"];
  /** Link "crie em ..." na barra de token. `null` = quem emite token nao e quem ve a tela. */
  linkTokens?: { href: string; label: string } | null;
  /** Chave do localStorage do token colado. Separada por perfil pra um token de
   *  averbadora nao reaparecer no campo do portal do banco. */
  storageKey?: string;
  /** Espelha a validacao do token pra pagina (o painel de credenciais do banco
   *  usa pra destacar QUAL token esta em uso). */
  onTokenInfoChange?: (info: TryTokenInfo) => void;
  /**
   * Credenciais do dono da tela, embutidas na propria barra de autorizacao em
   * vez de virarem uma secao a parte — `resumo` fica sempre visivel na barra e
   * `detalhe` abre no disclosure. O explorer nao sabe o que sao: quem monta e a
   * pagina (o portal do banco lista os tokens dele; a averbadora nao passa nada).
   */
  credenciais?: { resumo: ReactNode; detalhe: ReactNode };
  /**
   * Link pro `openapi.yaml` junto dos downloads de coleção. Default `false`.
   *
   * Fica desligado por default porque o spec descreve as QUATRO camadas: numa
   * tela de parceiro ele entrega a existência dos endpoints de averbadora,
   * prefeitura e servidor a quem só deveria ver a própria camada. As coleções,
   * essas sim, são por camada e aparecem sempre.
   */
  linkOpenapi?: boolean;
  /**
   * Servidores disponíveis (homologação/produção — deploys separados),
   * configurados pela averbadora em Configurações. O selecionado é a URL que
   * o curl de exemplo, a validação do token e o "Tente agora" usam, e também
   * o ambiente cujas credenciais a página deve listar — a API recusa uma chave
   * de homologação em produção e vice-versa. Sem essa prop (ou com 1 item), o
   * seletor não aparece e a página cai no `apiBaseUrl` fixo do deploy do web.
   */
  servers?: { key: string; label: string; url: string }[];
  /** Espelha o servidor ativo pra página (o painel de credenciais do banco usa
   *  pra listar só as chaves daquele ambiente). */
  onServerChange?: (key: string) => void;
}

export function ApiDocsExplorer({
  layers,
  layerInicial,
  linkTokens,
  storageKey = "atlas:try-token",
  onTokenInfoChange,
  credenciais,
  linkOpenapi = false,
  servers,
  onServerChange,
}: ApiDocsExplorerProps) {
  const [credAberto, setCredAberto] = useState(false);

  // Seletor de servidor: guardado a parte do token (trocar de ambiente nao
  // devia apagar o token colado) e persistido por pagina, como o token ja e'.
  const serverStorageKey = `${storageKey}:server`;
  const [serverKey, setServerKey] = useState<string>(() => {
    const saved = localStorage.getItem(serverStorageKey);
    return saved && servers?.some((s) => s.key === saved) ? saved : (servers?.[0]?.key ?? "");
  });
  useEffect(() => {
    if (serverKey || !servers?.length) return;
    const saved = localStorage.getItem(serverStorageKey);
    setServerKey(saved && servers.some((s) => s.key === saved) ? saved : servers[0]!.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servers]);
  const activeServer = servers?.find((s) => s.key === serverKey) ?? servers?.[0];
  const baseUrl = activeServer?.url || apiBaseUrl;

  // Espelha o servidor ativo pra pagina. Mesmo padrao do onTokenInfoChange
  // abaixo: callback num ref, efeito dependendo SO do valor — com a prop nas
  // deps, um callback inline mudaria a cada render e viraria loop.
  const serverCbRef = useRef(onServerChange);
  serverCbRef.current = onServerChange;
  useEffect(() => {
    if (activeServer) serverCbRef.current?.(activeServer.key);
  }, [activeServer]);

  const [layerKey, setLayerKey] = useState<Layer["key"]>(() => layerInicial ?? layers[0]!.key);
  // O `?? layers[0]` nao e' decorativo: um `layerInicial` que nao esta em
  // `layers` derrubaria a tela, em vez de cair na primeira camada disponivel.
  const layer = layers.find((l) => l.key === layerKey) ?? layers[0]!;
  // Secoes em acordeao (padrao Swagger): varias podem ficar abertas, e a
  // primeira ja vem aberta pra tela nao abrir vazia.
  const [abertas, setAbertas] = useState<Set<string>>(() => new Set([layer.sections[0]!.key]));
  const [search, setSearch] = useState("");
  // Modal de autorizacao. `draft` so vira token efetivo no "Autorizar" — assim a
  // validacao (que e' debounced no token) nao dispara a cada tecla digitada.
  const [authOpen, setAuthOpen] = useState(false);
  const [draft, setDraft] = useState("");
  // Shared API token used by every "Tente agora" on the page. Persisted so it
  // survives reloads and layer switches.
  const [token, setToken] = useState<string>(() => localStorage.getItem(storageKey) ?? "");
  function updateToken(v: string) {
    setToken(v);
    localStorage.setItem(storageKey, v);
  }

  // VALIDA o token contra a API em vez de so checar "tem texto". O indicador
  // antigo dizia "token definido" pra qualquer string colada — inclusive um
  // secret de webhook ou um token de outra camada — e o erro so aparecia ao
  // clicar em "tente agora". Agora /v1/external/me responde a camada e o dono,
  // e a pagina bloqueia as acoes que nao cabem. Cliente pediu 06/08/2026.
  // Uniao DISCRIMINADA (um membro por estado). Com {estado: "vazio"|"checando"|
  // "invalido"} num membro so, o TypeScript nao elimina o membro ao comparar o
  // campo — e o acesso a `camada` no ramo "ok" nao compilava.
  const [tokenInfo, setTokenInfo] = useState<TryTokenInfo>({ estado: "vazio" });
  useEffect(() => {
    const t = token.trim();
    if (!t) { setTokenInfo({ estado: "vazio" }); return; }
    setTokenInfo({ estado: "checando" });
    let cancelado = false;
    // debounce: o campo e' colado/digitado, nao dispara a cada tecla
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${baseUrl}/v1/external/me`, { headers: { Authorization: `Bearer ${t}` } });
        if (cancelado) return;
        if (!r.ok) { setTokenInfo({ estado: "invalido" }); return; }
        const j = (await r.json()) as { camada?: string; partner_id?: number; scopes?: string[]; token_id?: string; environment?: string };
        setTokenInfo({
          estado: "ok",
          camada: j.camada ?? "?",
          partnerId: j.partner_id ?? 0,
          scopes: j.scopes ?? [],
          // token_id permite a pagina casar o token colado com a credencial
          // listada por ID exato, em vez de adivinhar pelo prefixo.
          tokenId: j.token_id ?? "",
          environment: j.environment ?? "",
        });
      } catch {
        if (!cancelado) setTokenInfo({ estado: "invalido" });
      }
    }, 400);
    return () => { cancelado = true; clearTimeout(timer); };
    // baseUrl entra na dep pra revalidar ao trocar de servidor.
  }, [token, baseUrl]);

  // Espelha o resultado pra pagina. O callback fica num ref e o efeito depende
  // SO de tokenInfo: com a prop nas deps, um callback inline mudaria a cada
  // render, o efeito redispararia, o pai faria setState e viraria loop.
  const cbRef = useRef(onTokenInfoChange);
  cbRef.current = onTokenInfoChange;
  useEffect(() => { cbRef.current?.(tokenInfo); }, [tokenInfo]);

  // O token serve para a camada aberta? "Tente agora" so libera quando bate —
  // token de banco na aba Prefeitura sempre daria 403.
  const tokenServeAqui = tokenInfo.estado === "ok" && tokenInfo.camada === layer.key;
  const ehProducao = tokenInfo.estado === "ok" && tokenInfo.environment === "production";

  /**
   * Com um token VALIDO da camada aberta, a pagina lista so o que aquele token
   * alcanca. Cliente pediu 06/08/2026: se a caixa `banco:read` nao foi marcada,
   * o endpoint que exige banco:read nao deve nem aparecer — antes aparecia com
   * o selo do escopo e so dava 403 no clique, o que sugere "o sistema esta
   * quebrado" em vez de "esta chave nao tem essa permissao".
   *
   * SEM token valido nao filtra nada: a pagina tambem serve de catalogo pra
   * quem esta decidindo quais escopos pedir.
   */
  const escoposDoToken = tokenServeAqui && tokenInfo.estado === "ok" ? tokenInfo.scopes : null;
  const podeVer = (e: Endpoint) =>
    !escoposDoToken || e.scope === "—" || escoposDoToken.includes(e.scope);
  // Secoes que sobraram sem nenhum endpoint visivel somem junto.
  const secoesVisiveis = useMemo(
    () => layer.sections
      .map((s) => ({ ...s, endpoints: s.endpoints.filter(podeVer) }))
      .filter((s) => s.endpoints.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layer, escoposDoToken?.join(",")],
  );
  const totalEndpoints = layer.sections.reduce((n, s) => n + s.endpoints.length, 0);
  const visiveis = secoesVisiveis.reduce((n, s) => n + s.endpoints.length, 0);
  const ocultos = totalEndpoints - visiveis;

  // When the search box is non-empty, flatten every endpoint of the current
  // layer and match by method, path, summary, scope or filter names.
  const searchHits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const hits: { section: string; endpoint: Endpoint }[] = [];
    for (const s of secoesVisiveis) {
      for (const e of s.endpoints) {
        const hay = [e.method, e.path, e.summary, e.scope, ...(e.filters ?? []).map((f) => `${f.name} ${f.label}`)]
          .join(" ")
          .toLowerCase();
        if (hay.includes(q)) hits.push({ section: s.label, endpoint: e });
      }
    }
    return hits;
  }, [search, secoesVisiveis]);

  function switchLayer(k: Layer["key"]) {
    setLayerKey(k);
    const l = layers.find((x) => x.key === k);
    if (l) setAbertas(new Set([l.sections[0]!.key]));
  }

  function toggleSecao(k: string) {
    setAbertas((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }

  const [erroColecao, setErroColecao] = useState<string | null>(null);

  /**
   * Baixa a colecao e troca o `baseUrl` pelo SERVIDOR SELECIONADO antes de salvar.
   *
   * O arquivo estatico (`/colecoes/<cliente>-<camada>.json`, gerado no build por
   * scripts/colecoes.ts) traz a URL de producao, porque o gerador roda em Node,
   * fora do Workers, sem acesso a config em KV. Sem esta troca, quem baixasse
   * com "Homologacao" aberto importaria uma colecao apontando pra producao — e
   * so descobriria ao ver dado real na resposta.
   */
  async function baixarColecao(tipo: "insomnia" | "postman") {
    setErroColecao(null);
    try {
      const res = await fetch(`/colecoes/${tipo}-${layerKey}.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Record<string, unknown>;

      // Cada cliente guarda a variavel num formato proprio.
      let trocou = false;
      if (tipo === "postman") {
        const v = (json.variable as { key: string; value: string }[] | undefined)?.find((x) => x.key === "baseUrl");
        if (v) { v.value = baseUrl; trocou = true; }
      } else {
        const env = (json.resources as { _type: string; data?: Record<string, unknown> }[] | undefined)
          ?.find((r) => r._type === "environment");
        if (env?.data) { env.data.baseUrl = baseUrl; trocou = true; }
      }
      // Falhar alto: baixar uma colecao com a URL errada e' pior que nao baixar.
      if (!trocou) throw new Error("nao encontrei a variavel baseUrl na colecao");

      const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tipo}-${layerKey}${activeServer ? `-${activeServer.key}` : ""}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErroColecao(`Nao foi possivel gerar a colecao (${(e as Error).message}). Tente de novo.`);
    }
  }

  const rotuloEstado =
    tokenInfo.estado === "vazio" ? "Não autorizado"
      : tokenInfo.estado === "checando" ? "Verificando token…"
      : tokenInfo.estado === "invalido" ? "Token inválido ou revogado"
      : tokenServeAqui ? `Autorizado · ${tokenInfo.camada}${tokenInfo.partnerId ? ` #${tokenInfo.partnerId}` : ""} · ${tokenInfo.environment || "—"}`
      : `Token da camada ${tokenInfo.camada} — não vale aqui`;
  const corEstado =
    tokenInfo.estado === "ok"
      ? (tokenServeAqui ? "var(--emerald-500)" : "var(--gold-500)")
      : tokenInfo.estado === "invalido" ? "var(--danger-500)" : "var(--text-muted)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Seletor de camada — vira um segmentado compacto ("select a definition"
          do Swagger) em vez de 4 cards grandes. Some com uma camada so. */}
      {layers.length > 1 ? (
        <Tabs
          variant="pills"
          activeKey={layerKey}
          onChange={(k) => switchLayer(k as Layer["key"])}
          tabs={layers.map((l) => ({ key: l.key, label: l.label }))}
        />
      ) : null}

      {/* ---------- Seletor de servidor (homologação/produção) ----------
          O escolhido aqui é pra onde o curl de exemplo, a validação do token
          e o "Tente agora" apontam. A credencial é uma só e serve nos dois —
          isto só troca o endereço. Some com 1 servidor só (ou nenhum). */}
      {servers && servers.length > 1 ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          padding: "10px 14px", borderRadius: 10,
          border: "1px solid var(--border-strong)", background: "var(--surface)",
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
            Servidor
          </span>
          <Tabs
            variant="pills"
            activeKey={serverKey}
            onChange={(k) => { setServerKey(k); localStorage.setItem(serverStorageKey, k); }}
            tabs={servers.map((s) => ({ key: s.key, label: s.label }))}
          />
          <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {baseUrl}
          </code>
        </div>
      ) : null}

      {/* ---------- Barra Authorize (com as credenciais embutidas) ---------- */}
      <div style={{
        borderRadius: 10,
        border: `1px solid ${tokenServeAqui ? "var(--emerald-500)" : "var(--border-strong)"}`,
        background: "var(--bg-elev)",
        overflow: "hidden",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px" }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>{tokenServeAqui ? "🔒" : "🔓"}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 220 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: corEstado }}>{rotuloEstado}</span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {tokenServeAqui
                ? <>Aplicado a todos os <b>{totalEndpoints}</b> endpoints desta API.</>
                : <>Autorize uma vez e o token vale para todos os <b>{totalEndpoints}</b> endpoints.</>}
            </span>
          </div>
          <Button size="sm" onClick={() => { setDraft(token); setAuthOpen(true); }}>
            {token ? "Alterar" : "Authorize"}
          </Button>
          {token ? (
            <button
              type="button"
              onClick={() => updateToken("")}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text-muted)", textDecoration: "underline" }}
            >
              revogar sessão
            </button>
          ) : null}
        </div>

        {/* Linha do token em uso + credenciais emitidas. So vira botao quando ha
            detalhe pra abrir (o portal do banco passa; a averbadora nao). */}
        {credenciais ? (
          <RowButton
            onClick={() => setCredAberto((o) => !o)}
            titulo={credAberto ? "Ocultar credenciais" : "Ver as credenciais desta consignatária"}
            style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}
          >
            {token ? (
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                {token.slice(0, 16)}{token.length > 16 ? "…" : ""}
              </code>
            ) : null}
            {token ? <span style={{ color: "var(--text-dim)", fontSize: 12 }}>·</span> : null}
            <span style={{ fontSize: 12, color: "var(--text-muted)", flex: 1 }}>{credenciais.resumo}</span>
            {/* Rotulo explicito em cor de acento: o chevron sozinho nao dizia
                que a linha abre. */}
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--accent)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {credAberto ? "Ocultar" : "Ver credenciais"}
              <span style={{ fontSize: 10 }}>{credAberto ? "▾" : "▸"}</span>
            </span>
          </RowButton>
        ) : token ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            padding: "8px 16px", borderTop: "1px solid var(--border)", background: "var(--surface)",
          }}>
            <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
              {token.slice(0, 16)}{token.length > 16 ? "…" : ""}
            </code>
          </div>
        ) : null}

        {credenciais && credAberto ? (
          <div style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
            {credenciais.detalhe}
          </div>
        ) : null}
      </div>

      {/* Token de produção: as chamadas do "Tente agora" gravam de verdade.
          Na averbadora isso era risco de admin; no portal do banco quem abre a
          tela é operador, então o aviso passa a valer a pena nas duas. */}
      {ehProducao ? (
        <div style={{
          fontSize: 12, padding: "8px 12px", borderRadius: 8, color: "var(--text)",
          border: "1px solid var(--gold-500)", background: "color-mix(in srgb, var(--gold-500) 12%, transparent)",
        }}>
          <b>Token de produção.</b> As chamadas saem contra dados reais — um <code>POST</code> aqui averba de verdade.
          Para explorar sem consequência, use um token <code>atl_test_*</code>.
        </div>
      ) : null}

      {/* ---------- Colecoes pro cliente HTTP ----------
          Geradas do mesmo catalogo que monta esta pagina (vite.config.ts ->
          scripts/colecoes.ts), uma por camada: quem integra o banco nao recebe
          os endpoints de averbadora e prefeitura junto. Nao ha arquivo no repo
          pra sair de sincronia com o que esta na tela. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 14px", borderRadius: 10,
        border: "1px solid var(--border-strong)", background: "var(--surface)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 240 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Rodar no seu cliente HTTP</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Os <b>{totalEndpoints}</b> endpoints desta camada, em pastas, com bearer já configurado.
            Preencha <code>token</code> depois de importar — os arquivos não levam credencial.
            {activeServer ? <> A <code>baseUrl</code> vem do servidor selecionado (<b>{activeServer.label}</b>).</> : null}
          </span>
          {erroColecao ? (
            <span style={{ fontSize: 11.5, color: "var(--danger-500)" }}>{erroColecao}</span>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Rotulo fixo: o ambiente ja esta dito na linha de texto ao lado, e
              um botao que muda de nome ao trocar o servidor so pisca a cada
              clique no seletor. O arquivo baixado continua trazendo o ambiente
              no nome (ex.: insomnia-banco-sandbox.json). */}
          <Button size="sm" variant="ghost" onClick={() => baixarColecao("insomnia")}>
            ↓ Insomnia
          </Button>
          <Button size="sm" variant="ghost" onClick={() => baixarColecao("postman")}>
            ↓ Postman
          </Button>
          {/* Sem `download`: OpenAPI e' util aberto no navegador, e a URL e' o
              que se cola no "import from URL" dos dois clientes. Fora da visao
              de parceiro por default — o spec cobre as quatro camadas. */}
          {linkOpenapi ? (
            <a href="/openapi.yaml" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Button size="sm" variant="ghost" title="Especificação OpenAPI 3.1 de todas as camadas — importável por URL">
                OpenAPI
              </Button>
            </a>
          ) : null}
        </div>
      </div>

      {/* ---------- Busca ---------- */}
      <div style={{ position: "relative" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Buscar em ${totalEndpoints} endpoints — path, descrição, filtro…`}
          style={{ ...inp, width: "100%", paddingLeft: 34 }}
        />
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 14 }}>⌕</span>
        {search ? (
          <button type="button" onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 16 }}>×</button>
        ) : null}
      </div>

      {/* Deixa explicito que a lista esta filtrada — senao o operador procura um
          endpoint que ele sabe que existe e conclui que a pagina quebrou. */}
      {escoposDoToken && ocultos > 0 ? (
        <div style={{
          fontSize: 12, color: "var(--text-muted)", padding: "8px 12px", borderRadius: 8,
          border: "1px solid var(--border)", background: "var(--bg-elev-2)",
        }}>
          Mostrando os <b style={{ color: "var(--text)" }}>{visiveis}</b> endpoints que este token alcança;
          {" "}<b style={{ color: "var(--text)" }}>{ocultos}</b> ocultos por falta de escopo.
          {" "}Para ver o catálogo completo, revogue a sessão acima.
        </div>
      ) : null}

      {/* ---------- Lista ---------- */}
      {searchHits ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {searchHits.length} resultado{searchHits.length === 1 ? "" : "s"} para “{search}”
          </span>
          {searchHits.map(({ section: sec, endpoint: e }, i) => (
            <EndpointCard
              key={`${e.method}-${e.path}-${i}`}
              endpoint={e}
              token={token}
              habilitado={tokenServeAqui}
              producao={ehProducao}
              baseUrl={baseUrl}
              grupo={sec}
            />
          ))}
          {searchHits.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Nada encontrado. Tente outro termo.</p>
          ) : null}
        </div>
      ) : secoesVisiveis.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
          Este token não tem escopo para nenhum endpoint desta API.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {secoesVisiveis.map((s) => {
            const aberta = abertas.has(s.key);
            return (
              <section key={s.key} style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
                <RowButton
                  onClick={() => toggleSecao(s.key)}
                  titulo={aberta ? `Recolher ${s.label}` : `Ver os ${s.endpoints.length} endpoints de ${s.label}`}
                  style={{ padding: "12px 14px", background: aberta ? "var(--bg-elev-2)" : "var(--surface)" }}
                >
                  <span style={{ fontSize: 10, width: 10, color: "var(--accent)" }}>{aberta ? "▾" : "▸"}</span>
                  <b style={{ fontSize: 14 }}>{s.label}</b>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.description}
                  </span>
                  <span style={{
                    fontSize: 11, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums",
                    border: "1px solid var(--border-strong)", borderRadius: 999, padding: "1px 8px",
                  }}>
                    {s.endpoints.length}
                  </span>
                </RowButton>
                {aberta ? (
                  <div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--border)" }}>
                    {s.endpoints.map((e, i) => (
                      <EndpointCard
                        key={`${e.method}-${e.path}-${i}`}
                        endpoint={e}
                        token={token}
                        habilitado={tokenServeAqui}
                        producao={ehProducao}
                        baseUrl={baseUrl}
                      />
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      {/* ---------- Referencia comum a camada, recolhida ---------- */}
      <CatalogoErros camada={layerKey} />
      <Anexos camada={layerKey} />

      {/* ---------- Guia, recolhido pra nao competir com a lista ---------- */}
      <details style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)" }}>
        <summary style={{ padding: "12px 14px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
          Autenticação e assinatura de webhooks
        </summary>
        <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <pre style={preStyle}>{`curl ${baseUrl}${layer.sections[0]!.endpoints[0]!.path} \\
  -H "Authorization: Bearer ${layer.tokenPrefix}"`}</pre>
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "8px 0 0" }}>
              O token só acessa endpoints da sua camada. Tentar usar um token da consignatária em <code>/v1/external/servidor/*</code> retorna 403.
              Não use o secret do webhook (<code>whsec_…</code>) para autenticar — ele serve só para validar a assinatura das entregas.
            </p>
          </div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "0 0 8px" }}>
              Cada delivery inclui <code>X-Atlas-Signature: sha256=&lt;hex&gt;</code> = HMAC-SHA256 do body com o secret do webhook.
            </p>
            <pre style={preStyle}>{`// Node.js
import crypto from "crypto";
const hmac = crypto.createHmac("sha256", WEBHOOK_SECRET);
hmac.update(rawBody);
const expected = "sha256=" + hmac.digest("hex");
if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(req.headers["x-atlas-signature"]))) {
  return res.status(401).end();
}`}</pre>
          </div>
        </div>
      </details>

      {/* ---------- Modal Authorize ---------- */}
      {authOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setAuthOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 60, display: "grid", placeItems: "center",
            background: "rgba(0,0,0,.5)", padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 100%)", background: "var(--surface)", color: "var(--text)",
              border: "1px solid var(--border-strong)", borderRadius: 12, padding: 20,
              display: "flex", flexDirection: "column", gap: 12, boxShadow: "var(--shadow-md)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <b style={{ fontSize: 16 }}>Autorização</b>
              <button type="button" onClick={() => setAuthOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--text-muted)", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Autenticação por <b>Bearer token</b> no header <code>Authorization</code>. Camada{" "}
              <b>{layer.label}</b> (<code>/v1/external/{layer.key}/*</code>).
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>Token</span>
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { updateToken(draft.trim()); setAuthOpen(false); } }}
                placeholder="atl_test_… ou atl_live_…"
                style={{ ...inp, fontFamily: "var(--font-mono)" }}
              />
            </label>

            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
              Fica guardado neste navegador e é enviado em todas as chamadas do “Tente agora”.
              {linkTokens
                ? <> Emita em <a href={linkTokens.href} style={{ color: "var(--accent)" }}>{linkTokens.label}</a>.</>
                : <> A emissão é feita pela averbadora.</>}
            </p>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              {token ? (
                <Button size="sm" variant="ghost" onClick={() => { updateToken(""); setDraft(""); setAuthOpen(false); }}>
                  Revogar
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" onClick={() => setAuthOpen(false)}>Cancelar</Button>
              <Button size="sm" onClick={() => { updateToken(draft.trim()); setAuthOpen(false); }} disabled={!draft.trim()}>
                Autorizar
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const thRef: React.CSSProperties = {
  textAlign: "left", padding: "7px 12px", fontWeight: 700, fontSize: 11,
  color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".04em",
  borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
};
const tdRef: React.CSSProperties = {
  padding: "7px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top",
  color: "var(--text-muted)", lineHeight: 1.5,
};

/** Cor do status: 4xx e' erro do integrador, 5xx e' nosso. */
function corStatus(status: number): string {
  return status >= 500 ? "var(--amber-500)" : "var(--danger-500)";
}

/**
 * Catalogo de erros da camada.
 *
 * Conferido contra o codigo-fonte por banco.anexos.test.ts — um `throw new
 * HttpError` novo num handler sem entrada no catalogo derruba o teste. E' o que
 * faz esta tabela continuar valendo depois do proximo endpoint.
 */
function CatalogoErros({ camada }: { camada: string }) {
  // Camada sem catalogo publicado nao ganha secao vazia — a ausencia e honesta.
  const catalogo = CODIGOS_ERRO_POR_CAMADA[camada] ?? [];
  if (!catalogo.length) return null;
  const globais = catalogo.filter((e) => e.origem === "middleware");
  const especificos = catalogo.filter((e) => e.origem !== "middleware");
  return (
    <details style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)" }}>
      <summary style={{ padding: "12px 14px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
        Códigos de erro <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({catalogo.length})</span>
      </summary>
      <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
          Toda falha responde no mesmo envelope. <code>details</code> só aparece em <code>validation_error</code>{" "}
          (mapa campo → motivo) e em <code>rate_limited</code> (<code>reset_in</code>, em segundos). Guarde o{" "}
          <code>trace_id</code>: é por ele que um chamado é rastreado.
        </p>
        <pre style={preStyle}>{`{
  "error": {
    "code": "validation_error",
    "message": "Dados invalidos",
    "details": { "parcelas": "Prazo maximo pro convenio X e 84x." },
    "trace_id": "01J…"
  }
}`}</pre>
        {[
          { titulo: "Valem para todos os endpoints (middleware)", itens: globais },
          { titulo: "Dependem do endpoint e do estado", itens: especificos },
        ].map((grupo) => (
          <div key={grupo.titulo}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
              {grupo.titulo}
            </div>
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
                  <thead>
                    <tr>{["HTTP", "code", "Quando acontece", "O que fazer"].map((h) => <th key={h} style={thRef}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {grupo.itens.map((e) => (
                      <tr key={e.codigo}>
                        <td style={{ ...tdRef, whiteSpace: "nowrap" }}>
                          <b style={{ color: corStatus(e.status), fontSize: 12 }}>{e.status}</b>
                        </td>
                        <td style={{ ...tdRef, whiteSpace: "nowrap" }}>
                          <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text)" }}>{e.codigo}</code>
                        </td>
                        <td style={tdRef}>{e.quando}</td>
                        <td style={tdRef}>{e.acao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/** Tabelas de enum. Ações e eventos de webhook vêm da fonte, não de cópia. */
function Anexos({ camada }: { camada: string }) {
  const anexos = ANEXOS_POR_CAMADA[camada] ?? [];
  if (!anexos.length) return null;
  return (
    <details style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)" }}>
      <summary style={{ padding: "12px 14px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
        Anexos <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({anexos.length} tabelas)</span>
      </summary>
      <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 16 }}>
        {anexos.map((a) => (
          <div key={a.titulo}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: a.nota ? 4 : 6 }}>{a.titulo}</div>
            {a.nota ? <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 6px", lineHeight: 1.5 }}>{a.nota}</p> : null}
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
                  <thead>
                    <tr>{["Valor", "Descrição"].map((h) => <th key={h} style={thRef}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {a.itens.map((i) => (
                      <tr key={i.valor}>
                        <td style={{ ...tdRef, whiteSpace: "nowrap" }}>
                          <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text)" }}>{i.valor}</code>
                        </td>
                        <td style={tdRef}>{i.descricao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * Camada a que um endpoint pertence, lida do proprio path
 * (`/v1/external/<camada>/...`).
 *
 * Derivar do path, e nao receber por prop, porque o `EndpointCard` e' renderizado
 * fundo na arvore e a camada ja esta ali, sem ambiguidade — passar prop so criaria
 * uma segunda fonte da mesma informacao, que pode divergir.
 */
function camadaDoPath(path: string): string {
  return /^\/v1\/external\/([a-z]+)\//.exec(path)?.[1] ?? "";
}

/** "Possíveis críticas" do endpoint — só os códigos que ESTA rota pode emitir. */
function TabelaCriticas({ codigos, camada }: { codigos: string[]; camada: string }) {
  const catalogo = CODIGOS_ERRO_POR_CAMADA[camada] ?? [];
  const itens = codigos
    .map((c) => catalogo.find((e) => e.codigo === c))
    .filter((e): e is (typeof catalogo)[number] => !!e);
  if (!itens.length) return null;
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{
        padding: "8px 12px", background: "var(--bg-elev-2)", borderBottom: "1px solid var(--border)",
        fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em",
      }}>
        Possíveis críticas
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
          <tbody>
            {itens.map((e) => (
              <tr key={e.codigo}>
                <td style={{ ...tdRef, whiteSpace: "nowrap", borderTop: "none" }}>
                  <b style={{ color: corStatus(e.status), fontSize: 12 }}>{e.status}</b>{" "}
                  <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text)" }}>{e.codigo}</code>
                </td>
                <td style={{ ...tdRef, borderTop: "none" }}>{e.quando}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "6px 12px", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--text-dim)" }}>
        Além destas, <code>401</code>, <code>403</code> e <code>429</code> valem para toda a camada — ver Códigos de erro.
      </div>
    </div>
  );
}

/**
 * Campos do corpo da requisicao, no formato de manual de integracao bancaria.
 *
 * Nao e escrita a mao: sai de `apps/api/scripts/gen-schemas.ts`, que le os
 * schemas zod que a API usa pra validar. Formato e obrigatoriedade vem dos
 * validadores; a descricao vem dos `.describe()`. Se a validacao mudar e o texto
 * nao acompanhar, quem mexeu tropeca no campo — nao existe documento paralelo
 * pra apodrecer.
 */
/** Achata a arvore em linhas, guardando o nivel pra indentar a sub-tabela. */
function linhasDe(campos: CampoDoc[], nivel = 0, prefixo = ""): { cp: CampoDoc; nivel: number; chave: string }[] {
  return campos.flatMap((cp) => {
    const chave = `${prefixo}${cp.nome}`;
    return [{ cp, nivel, chave }, ...(cp.campos ? linhasDe(cp.campos, nivel + 1, `${chave}.`) : [])];
  });
}

/**
 * Duas variantes, porque a terceira coluna quer dizer coisas diferentes nos dois
 * lados. Na requisicao, "obrigatorio" e' o que VOCE precisa mandar. Na resposta,
 * o que importa e' se a chave sempre vem — e os schemas de saida usam `nullable`
 * justamente pra ela vir sempre, com `null` no lugar do valor. Reaproveitar o
 * rotulo "obrigatorio" na resposta faria o integrador programar defesa contra
 * chave ausente que nunca vai acontecer.
 */
function TabelaCampos({ campos, variante = "requisicao" }: { campos: CampoDoc[]; variante?: "requisicao" | "resposta" }) {
  const resposta = variante === "resposta";
  const linhas = linhasDe(campos);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{
        padding: "8px 12px", background: "var(--bg-elev-2)", borderBottom: "1px solid var(--border)",
        fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em",
      }}>
        {resposta ? "Campos da resposta (data)" : "Campos do corpo"}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
          <thead>
            <tr>
              {["Campo", "Formato", resposta ? "Presença" : "Obrigatório", "Descrição"].map((h) => (
                <th key={h} style={{
                  textAlign: "left", padding: "7px 12px", fontWeight: 700, fontSize: 11,
                  color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".04em",
                  borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ cp, nivel, chave }) => (
              <tr key={chave}>
                <td style={{ padding: "7px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top", paddingLeft: 12 + nivel * 18 }}>
                  {nivel > 0 ? <span style={{ color: "var(--text-dim)", marginRight: 4 }}>└</span> : null}
                  <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{cp.nome}</code>
                </td>
                <td style={{ padding: "7px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                  {cp.formato}
                </td>
                <td style={{ padding: "7px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {resposta ? (
                    <span style={{ fontSize: 11, color: cp.obrigatorio ? "var(--text-muted)" : "var(--text-dim)" }}>
                      {cp.obrigatorio ? "sempre" : "quando houver"}
                      {cp.nulavel ? <span style={{ color: "var(--text-dim)" }}> · pode ser <code style={{ fontSize: 10 }}>null</code></span> : null}
                    </span>
                  ) : cp.obrigatorio ? (
                    <b style={{ color: "var(--danger-500)", fontSize: 11 }}>SIM</b>
                  ) : (
                    <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                      não{cp.padrao ? ` · padrão ${cp.padrao}` : ""}
                    </span>
                  )}
                </td>
                <td style={{ padding: "7px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top", color: "var(--text-muted)", lineHeight: 1.5 }}>
                  {cp.descricao}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BaseUrlBadge() {
  return (
    <code style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, background: "var(--bg-elev-2)", color: "var(--text-muted)" }}>
      {apiBaseUrl}
    </code>
  );
}

function EndpointCard({ endpoint, token, habilitado, producao, baseUrl, grupo }: { endpoint: Endpoint; token: string; habilitado: boolean; producao: boolean; baseUrl: string; grupo?: string }) {
  const [open, setOpen] = useState(false);
  const [pathParam, setPathParam] = useState<string>(endpoint.examplePathParam ?? "");
  const [body, setBody] = useState<string>(endpoint.exampleBody ? JSON.stringify(endpoint.exampleBody, null, 2) : "");
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((endpoint.filters ?? []).map((f) => [f.name, f.example ?? ""])),
  );
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<{ status: number; body: string } | null>(null);
  const [csvNome, setCsvNome] = useState<string>("");
  // Endpoints de importacao recebem o arquivo cru, nao JSON.
  const aceitaCsv = endpoint.method === "POST" && /\/importar$/.test(endpoint.path);

  const finalPath = useMemo(() => {
    let p = endpoint.path;
    if (endpoint.examplePathParam && pathParam) p = p.replace(/\{[^}]+\}/, pathParam);
    const qs = new URLSearchParams();
    for (const f of endpoint.filters ?? []) {
      const v = filterValues[f.name]?.trim();
      if (v) qs.set(f.name, v);
    }
    const query = qs.toString();
    return query ? `${p}?${query}` : p;
  }, [endpoint, pathParam, filterValues]);

  const activeFilterCount = useMemo(
    () => (endpoint.filters ?? []).filter((f) => filterValues[f.name]?.trim()).length,
    [endpoint.filters, filterValues],
  );

  // Tabela campo a campo, derivada do schema zod que a API usa pra validar.
  // Existe so pros endpoints ja migrados pra schema nomeado — os demais seguem
  // com o exemplo de corpo ate a migracao alcancar.
  const chaveDoc = `${endpoint.method} ${endpoint.path}`;
  const campos = CAMPOS_POR_ENDPOINT[chaveDoc];
  // A tabela de resposta so existe onde o handler aplica `parse()` na saida.
  // Onde nao existe, nao ha contrato garantido — e melhor nao publicar tabela
  // nenhuma do que publicar uma que a API nao e obrigada a cumprir.
  const camposResposta = CAMPOS_RESPOSTA_POR_ENDPOINT[chaveDoc];
  const criticas = CRITICAS_POR_ENDPOINT[chaveDoc];

  async function run() {
    // Token de producao + metodo que grava = confirmacao explicita. O "Tente
    // agora" nao e simulacao: com atl_live_* ele averba, quita e importa CSV
    // contra dados reais. Um GET nao pede nada.
    if (producao && endpoint.method !== "GET") {
      const ok = window.confirm(
        `PRODUÇÃO — esta chamada grava de verdade.\n\n${endpoint.method} ${endpoint.path}\n\n${endpoint.summary}\n\nConfirmar o envio?`,
      );
      if (!ok) return;
    }
    setLoading(true);
    setResponse(null);
    try {
      const res = await fetch(`${baseUrl}${finalPath}`, {
        method: endpoint.method,
        headers: {
          // CSV vai como text/csv; o backend aceita os dois formatos, mas mandar
          // um CSV rotulado como JSON faria o parse falhar antes de chegar nele.
          "Content-Type": aceitaCsv && csvNome ? "text/csv" : "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: ["POST", "PATCH"].includes(endpoint.method) && body ? body : undefined,
      });
      const text = await res.text();
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setResponse({ status: res.status, body: pretty });
    } catch (err) {
      setResponse({ status: 0, body: String(err) });
    } finally {
      setLoading(false);
    }
  }

  const methodColor: Record<Method, string> = { GET: "#3b82f6", POST: "#10b981", PATCH: "#f59e0b", DELETE: "#ef4444" };

  return (
    <div style={{
      // Dentro de uma secao os itens sao linhas de uma lista: separador fino em
      // vez de card com borda propria. Na busca ficam soltos, e aí a borda volta.
      border: grupo ? "1px solid var(--border)" : "none",
      borderRadius: grupo ? 10 : 0,
      borderTop: grupo ? undefined : "1px solid var(--border)",
      overflow: "hidden",
      background: open ? "var(--bg-elev-2)" : "transparent",
    }}>
      <RowButton
        onClick={() => setOpen((o) => !o)}
        titulo={open ? "Recolher" : "Ver detalhes e testar este endpoint"}
        style={{ padding: "10px 14px" }}
      >
        <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 10, letterSpacing: ".04em", padding: "3px 0", borderRadius: 4, background: methodColor[endpoint.method], color: "white", minWidth: 54, textAlign: "center" }}>{endpoint.method}</span>
        <code style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{endpoint.path}</code>
        <span style={{ fontSize: 12, color: "var(--text-muted)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {grupo ? <span style={{ color: "var(--text-dim)" }}>{grupo} · </span> : null}
          {endpoint.summary}
        </span>
        {endpoint.filters?.length ? (
          <span title={`${endpoint.filters.length} filtros disponíveis`} style={{ fontSize: 11, fontWeight: 600, color: activeFilterCount > 0 ? "var(--accent)" : "var(--text-dim)", whiteSpace: "nowrap" }}>
            ⚲ {activeFilterCount > 0 ? activeFilterCount : endpoint.filters.length}
          </span>
        ) : null}
        {/* Escopo em texto discreto, com cadeado. O Pill colorido roubava a
            atencao do path, que e' a informacao que o integrador procura. */}
        {endpoint.scope !== "—" ? (
          <code title={`Escopo exigido: ${endpoint.scope}`} style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
            🔒 {endpoint.scope}
          </code>
        ) : null}
        <span style={{ fontSize: 10, color: "var(--accent)" }}>{open ? "▾" : "▸"}</span>
      </RowButton>
      {open ? (
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          {!habilitado ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 10px", border: "1px dashed var(--border-strong)", borderRadius: 8 }}>
              {!token
                ? <>Cole um API token válido desta camada na barra <b>“API token para testar”</b> no topo para liberar o “Tente agora”.</>
                : <>O token do topo não vale para esta camada (ou é inválido). Confira o indicador ao lado do campo.</>}
            </div>
          ) : null}
          {/* Upload de CSV para os endpoints de importacao — em vez de o operador
              colar o arquivo inteiro na caixa de JSON. So aparece onde faz
              sentido, e o botao so libera com token valido DESTA camada. */}
          {aceitaCsv ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "var(--bg-elev-2)" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                Arquivo CSV
              </span>
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={!habilitado}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setCsvNome(f.name);
                  setBody(await f.text());
                }}
                style={{ fontSize: 13, color: "var(--text)" }}
              />
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {csvNome
                  ? <>Carregado: <b>{csvNome}</b> — clique em “Tente agora” para enviar.</>
                  : <>Selecione o CSV. O conteúdo vai no corpo da requisição como <code>text/csv</code>.</>}
              </span>
            </div>
          ) : null}
          {endpoint.examplePathParam ? (
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>Parâmetro de path</span>
              <input value={pathParam} onChange={(e) => setPathParam(e.target.value)} style={inp} />
            </label>
          ) : null}
          {endpoint.filters?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "var(--bg-elev-2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>Filtros (query string)</span>
                {activeFilterCount > 0 ? (
                  <button type="button" onClick={() => setFilterValues(Object.fromEntries((endpoint.filters ?? []).map((f) => [f.name, ""])))}
                    style={{ fontSize: 11, color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                    limpar
                  </button>
                ) : null}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                {endpoint.filters.map((f) => (
                  <label key={f.name} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{f.label} <code style={{ fontSize: 10, color: "var(--text-muted)" }}>{f.name}</code></span>
                    {f.type === "enum" ? (
                      <select value={filterValues[f.name] ?? ""} onChange={(e) => setFilterValues((v) => ({ ...v, [f.name]: e.target.value }))} style={inp}>
                        {(f.options ?? []).map((o) => <option key={o} value={o}>{o === "" ? "— todos —" : o}</option>)}
                      </select>
                    ) : (
                      <input value={filterValues[f.name] ?? ""} onChange={(e) => setFilterValues((v) => ({ ...v, [f.name]: e.target.value }))} placeholder={f.placeholder} style={inp} />
                    )}
                    {f.hint ? <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{f.hint}</span> : null}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          {campos?.length ? <TabelaCampos campos={campos} /> : null}
          {camposResposta?.length ? <TabelaCampos campos={camposResposta} variante="resposta" /> : null}
          {criticas?.length ? <TabelaCriticas codigos={criticas} camada={camadaDoPath(endpoint.path)} /> : null}
          {/* Content-types aceitos, quando a rota recebe mais que JSON. O
              console e' a documentacao do produto: anunciar so o campo de JSON
              numa rota que tambem recebe zip e planilha esconderia metade do
              contrato de quem le aqui em vez de ler o openapi. */}
          {endpoint.contentTypes?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>Content-Type aceito</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {endpoint.contentTypes.map((t) => (
                  <code key={t} style={{ fontSize: 11, color: "var(--text-muted)" }}>{t}</code>
                ))}
              </div>
              {endpoint.bodyDescription ? (
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{endpoint.bodyDescription}</span>
              ) : null}
            </div>
          ) : null}
          {["POST", "PATCH"].includes(endpoint.method) && endpoint.exampleBody ? (
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>Body (JSON)</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} style={{ ...inp, fontFamily: "var(--font-mono)", fontSize: 12 }} />
            </label>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            {/* So libera com token VALIDO desta camada — e, nos endpoints de
                importacao, so depois de escolher o arquivo. Antes bastava ter
                texto no campo e o clique ia direto pro 401/403. */}
            <Button size="sm" onClick={run} disabled={loading || !habilitado || (aceitaCsv && !csvNome)}>
              {loading ? "Enviando…" : aceitaCsv ? "Importar CSV →" : "Tente agora →"}
            </Button>
            <code style={{ fontSize: 12, color: "var(--text-muted)", alignSelf: "center" }}>{endpoint.method} {baseUrl}{finalPath}</code>
          </div>
          {response ? (
            <div>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                <b>Status:</b>{" "}
                <Pill variant={response.status >= 200 && response.status < 300 ? "emdia" : response.status === 0 ? "expirado" : "pendente"}>
                  {response.status === 0 ? "ERRO" : response.status}
                </Pill>
              </div>
              {response.status === 401 ? (
                <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 6 }}>
                  401 = token ausente ou inválido. Confira se colou um <code>atl_*</code> válido na barra do topo — o secret do webhook (<code>whsec_…</code>) não funciona aqui.
                </div>
              ) : null}
              {response.status === 403 ? (
                <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 6 }}>
                  403 = token de camada errada ou sem escopo. Use um token da camada desta API e com o escopo exigido pelo endpoint.
                </div>
              ) : null}
              <pre style={preStyle}>{response.body}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const inp: React.CSSProperties = { padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text)", fontSize: 14 };
const preStyle: React.CSSProperties = { padding: 12, background: "var(--bg-elev-2)", borderRadius: 8, overflow: "auto", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text)", margin: 0, maxHeight: 360 };
